import React, { useState } from 'react';
import { Card, Tabs, Table, Input, Button, Space, Tag, Avatar, Typography, Row, Col, Select, Pagination, Modal, Spin, Collapse, InputNumber } from 'antd';
import { SearchOutlined, UserOutlined, StarOutlined, EyeOutlined, ProfileOutlined, FilterOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import apiService from '../../../services/api';
import { MenteeSearchResponse, MentorSearchResponse, CandidateFilterRequest } from '../../../types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { Panel } = Collapse;

const CandidateSearchTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('mentees');
  const [menteesSearchText, setMenteesSearchText] = useState('');
  const [mentorsSearchText, setMentorsSearchText] = useState('');
  const [menteesPagination, setMenteesPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [mentorsPagination, setMentorsPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [candidateDetailLoading, setCandidateDetailLoading] = useState(false);
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);
  const [menteesFilters, setMenteesFilters] = useState<CandidateFilterRequest>({});
  const [mentorsFilters, setMentorsFilters] = useState<CandidateFilterRequest>({});

  const { data: categories = [] } = useQuery({
    queryKey: companyProfileQueryKeys.categories,
    queryFn: async () => {
      const r = await apiService.getCategories();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data: allMentorsData } = useQuery({
    queryKey: companyProfileQueryKeys.mentors,
    queryFn: async () => {
      const r = await apiService.searchMentors({ page: 1, pageSize: 100 });
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 10,
    enabled: activeTab === 'mentees' && advancedFiltersVisible,
  });
  const allMentors = allMentorsData?.data ?? [];

  const { data: favorites = [] } = useQuery({
    queryKey: companyProfileQueryKeys.favorites({}),
    queryFn: async () => {
      const r = await apiService.getCandidateFavorites();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: pool = [] } = useQuery({
    queryKey: companyProfileQueryKeys.pool({}),
    queryFn: async () => {
      const r = await apiService.getCandidatePool();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const menteesParams = {
    page: menteesPagination.current,
    pageSize: menteesPagination.pageSize,
    searchTerm: menteesSearchText || menteesFilters.searchTerm || undefined,
    skill: menteesFilters.skill || undefined,
    approvedSkill: menteesFilters.approvedSkill || undefined,
    project: menteesFilters.project || undefined,
    bootcampTopic: menteesFilters.bootcampTopic || undefined,
    mentorId: menteesFilters.mentorId,
    minMentorHours: menteesFilters.minMentorHours,
    maxMentorHours: menteesFilters.maxMentorHours,
    minExperience: menteesFilters.minExperience,
    maxExperience: menteesFilters.maxExperience,
    location: menteesFilters.location || undefined
  };
  const { data: menteesResult, isLoading: menteesLoading, isFetching: menteesFetching } = useQuery({
    queryKey: ['company-panel', 'candidate-search', 'mentees', menteesParams],
    queryFn: async () => {
      const r = await apiService.searchMentees(menteesParams);
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    enabled: activeTab === 'mentees',
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });
  const mentees = menteesResult?.data ?? [];
  const menteesTotal = menteesResult?.totalCount ?? 0;

  const mentorsParams = {
    page: mentorsPagination.current,
    pageSize: mentorsPagination.pageSize,
    searchTerm: mentorsSearchText || mentorsFilters.searchTerm || undefined,
    category: (mentorsFilters as any).category || undefined,
    skill: mentorsFilters.skill || undefined,
    minExperience: mentorsFilters.minExperience,
    maxExperience: mentorsFilters.maxExperience,
    location: mentorsFilters.location || undefined
  };
  const { data: mentorsResult, isLoading: mentorsLoading, isFetching: mentorsFetching } = useQuery({
    queryKey: ['company-panel', 'candidate-search', 'mentors', mentorsParams],
    queryFn: async () => {
      const r = await apiService.searchMentors({
        ...mentorsParams,
        hasAvailability: undefined
      });
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    enabled: activeTab === 'mentors',
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });
  const mentors = mentorsResult?.data ?? [];
  const mentorsTotal = mentorsResult?.totalCount ?? 0;

  const handleMenteesSearch = () => {
    setMenteesPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleMentorsSearch = () => {
    setMentorsPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetail = async (candidate: MenteeSearchResponse | MentorSearchResponse, type: 'mentee' | 'mentor') => {
    setSelectedCandidate({ ...candidate, type });
    setCandidateDetailLoading(true);
    setDetailModalVisible(true);
    
    try {
      if (type === 'mentee') {
        // Temel bilgileri yükle
        const response = await apiService.getMenteeDetail(candidate.id);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MenteeDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.title = response.data.title || null;
          detail.hourlyRate = null; // Mentee'de yok
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMenteeSkillsById(candidate.id),
              apiService.getMenteeEducationsById(candidate.id),
              apiService.getMenteeWorkExperiencesById(candidate.id),
              apiService.getMenteeProjectsById(candidate.id),
              apiService.getMenteeCertificatesById(candidate.id)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentee detail sections:', err);
          }
          
          setSelectedCandidate({ ...candidate, type, detail });
        } else {
          toast.error('Mentee detayları yüklenemedi');
        }
      } else {
        // Temel bilgileri yükle
        const response = await apiService.getMentorDetail(candidate.id);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MentorDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.hourlyRate = response.data.hourlyRate;
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMentorSkills(candidate.id),
              apiService.getMentorEducations(candidate.id),
              apiService.getMentorWorkExperiences(candidate.id),
              apiService.getMentorProjects(candidate.id),
              apiService.getMentorCertificates(candidate.id)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentor detail sections:', err);
          }
          
          setSelectedCandidate({ ...candidate, type, detail });
        } else {
          toast.error('Mentor detayları yüklenemedi');
        }
      }
    } catch (error) {
      console.error('Error loading candidate detail:', error);
      toast.error('Aday detayları yüklenirken hata oluştu');
    } finally {
      setCandidateDetailLoading(false);
    }
  };

  const addToFavoritesMutation = useMutation({
    mutationFn: (data: { menteeId: number | null; mentorId: number | null; notes: string }) =>
      apiService.addToFavorites(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday favorilere eklendi');
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.favorites({}) });
      } else {
        toast.error(response.message || 'Favorilere eklenirken hata oluştu');
      }
    },
    onError: () => toast.error('Favorilere eklenirken hata oluştu'),
  });

  const addToPoolMutation = useMutation({
    mutationFn: (data: { menteeId: number | null; mentorId: number | null; notes: string; status: string }) =>
      apiService.addToPool(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday havuza eklendi');
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.pool({}) });
      } else {
        toast.error(response.message || 'Havuza eklenirken hata oluştu');
      }
    },
    onError: () => toast.error('Havuza eklenirken hata oluştu'),
  });

  const isInFavorites = (candidateId: number, type: 'mentee' | 'mentor') => {
    return favorites.some(fav =>
      (type === 'mentee' && fav.menteeId === candidateId) ||
      (type === 'mentor' && fav.mentorId === candidateId)
    );
  };

  const isInPool = (candidateId: number, type: 'mentee' | 'mentor') => {
    return pool.some(p =>
      (type === 'mentee' && p.menteeId === candidateId) ||
      (type === 'mentor' && p.mentorId === candidateId)
    );
  };

  const handleAddToFavorites = (candidate: MenteeSearchResponse | MentorSearchResponse, type: 'mentee' | 'mentor') => {
    addToFavoritesMutation.mutate({
      menteeId: type === 'mentee' ? candidate.id : null,
      mentorId: type === 'mentor' ? candidate.id : null,
      notes: 'Aday Ara ekranından eklendi'
    });
  };

  const handleAddToPool = (candidate: MenteeSearchResponse | MentorSearchResponse, type: 'mentee' | 'mentor') => {
    addToPoolMutation.mutate({
      menteeId: type === 'mentee' ? candidate.id : null,
      mentorId: type === 'mentor' ? candidate.id : null,
      notes: 'Aday Ara ekranından eklendi',
      status: 'Available'
    });
  };

  const menteeColumns = [
    {
      title: 'Profil',
      key: 'profile',
      width: 80,
      render: (_: any, record: MenteeSearchResponse) => (
        <Avatar 
          size={40} 
          src={record.profileImage ? `data:image/jpeg;base64,${record.profileImage}` : undefined}
          icon={<UserOutlined />}
          style={{ backgroundColor: 'var(--primary-500)' }}
        />
      ),
    },
    {
      title: 'Ad Soyad',
      key: 'fullName',
      render: (_: any, record: MenteeSearchResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Deneyim',
      dataIndex: 'experienceYears',
      key: 'experienceYears',
      render: (years: number | null | undefined) => {
        if (years === null || years === undefined) {
          return <Text type="secondary">İş deneyimi yok</Text>;
        }
        return `${years} yıl`;
      },
    },
    {
      title: 'Lokasyon',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location || '-',
    },
    {
      title: 'Yetkinlikler',
      dataIndex: 'skills',
      key: 'skills',
      render: (skills: string[]) => (
        <div>
          {skills?.slice(0, 2).map((skill, index) => (
            <Tag key={index} color="blue" style={{ margin: '2px' }}>
              {skill}
            </Tag>
          ))}
          {skills?.length > 2 && <Tag color="default">+{skills.length - 2}</Tag>}
        </div>
      ),
    },
    {
      title: 'Referanslar',
      key: 'referencedByMentors',
      render: (_: any, record: MenteeSearchResponse) => {
        const refs = record.referencedByMentors;
        if (!refs?.length) return <Text type="secondary">—</Text>;
        return (
          <Space wrap size={[4, 4]}>
            {refs.slice(0, 2).map((m) => (
              <Tag key={m.id} color="purple">{m.firstName} {m.lastName}</Tag>
            ))}
            {refs.length > 2 && <Tag color="default">+{refs.length - 2}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MenteeSearchResponse) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record, 'mentee')}
            style={{ color: 'var(--primary-500)' }}
          >
            Görüntüle
          </Button>
          <Button 
            type="text" 
            icon={<ProfileOutlined />}
            onClick={() => window.open(`/mentee/${record.id}`, '_blank')}
            style={{ color: 'var(--primary-500)' }}
          >
            Profil Görüntüle
          </Button>
          <Button
            type="text"
            onClick={() => handleAddToFavorites(record, 'mentee')}
            disabled={isInFavorites(record.id, 'mentee')}
            loading={addToFavoritesMutation.isPending}
          >
            {isInFavorites(record.id, 'mentee') ? 'Favoride' : 'Favori'}
          </Button>
          <Button
            type="text"
            onClick={() => handleAddToPool(record, 'mentee')}
            disabled={isInPool(record.id, 'mentee')}
            loading={addToPoolMutation.isPending}
          >
            {isInPool(record.id, 'mentee') ? 'Havuzda' : 'Havuza Ekle'}
          </Button>
        </Space>
      ),
    },
  ];

  const mentorColumns = [
    {
      title: 'Profil',
      key: 'profile',
      width: 80,
      render: (_: any, record: MentorSearchResponse) => (
        <Avatar 
          size={40} 
          src={record.profileImage ? `data:image/jpeg;base64,${record.profileImage}` : undefined}
          icon={<UserOutlined />}
          style={{ backgroundColor: 'var(--success)' }}
        />
      ),
    },
    {
      title: 'Ad Soyad',
      key: 'fullName',
      render: (_: any, record: MentorSearchResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.email || record.title || 'Mentör'}</div>
        </div>
      ),
    },
    {
      title: 'Deneyim',
      dataIndex: 'experienceYears',
      key: 'experienceYears',
      render: (years: number | null | undefined) => {
        if (years === null || years === undefined) {
          return <Text type="secondary">İş deneyimi yok</Text>;
        }
        return `${years} yıl`;
      },
    },
    {
      title: 'Puan',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <StarOutlined style={{ color: '#faad14' }} />
          <span>{rating?.toFixed(1) || '0.0'}</span>
        </div>
      ),
    },
    {
      title: 'Lokasyon',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location || '-',
    },
    {
      title: 'Yetkinlikler',
      dataIndex: 'skills',
      key: 'skills',
      render: (skills: string[]) => (
        <div>
          {skills?.slice(0, 2).map((skill, index) => (
            <Tag key={index} color="green" style={{ margin: '2px' }}>
              {skill}
            </Tag>
          ))}
          {skills?.length > 2 && <Tag color="default">+{skills.length - 2}</Tag>}
        </div>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorSearchResponse) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record, 'mentor')}
            style={{ color: 'var(--primary-500)' }}
          >
            Görüntüle
          </Button>
          <Button 
            type="text" 
            icon={<ProfileOutlined />}
            onClick={() => window.open(`/mentor/${record.id}`, '_blank')}
            style={{ color: 'var(--primary-500)' }}
          >
            Profil Görüntüle
          </Button>
          <Button
            type="text"
            onClick={() => handleAddToFavorites(record, 'mentor')}
            disabled={isInFavorites(record.id, 'mentor')}
            loading={addToFavoritesMutation.isPending}
          >
            {isInFavorites(record.id, 'mentor') ? 'Favoride' : 'Favori'}
          </Button>
          <Button
            type="text"
            onClick={() => handleAddToPool(record, 'mentor')}
            disabled={isInPool(record.id, 'mentor')}
            loading={addToPoolMutation.isPending}
          >
            {isInPool(record.id, 'mentor') ? 'Havuzda' : 'Havuza Ekle'}
          </Button>
        </Space>
      ),
    },
  ];

  const listLoading = menteesLoading || mentorsLoading;
  const tableLoadingMentees = menteesLoading || menteesFetching;
  const tableLoadingMentors = mentorsLoading || mentorsFetching;

  return (
    <div className="panel-tab-content">
      <TabListLoading loading={listLoading}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
              Aday Ara
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Menteeler ve mentörler arasından uygun adayları bulun
            </Text>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Menteeler" key="mentees">
            <Card>
              <Row style={{ marginBottom: '16px' }} gutter={[8, 8]}>
                <Col xs={24} sm={12} md={10}>
                  <Input
                    placeholder="Mentee ara..."
                    prefix={<SearchOutlined />}
                    value={menteesSearchText}
                    onChange={(e) => setMenteesSearchText(e.target.value)}
                    onPressEnter={handleMenteesSearch}
                    style={{ borderRadius: '6px' }}
                  />
                </Col>
                <Col xs={24} sm={6} md={4}>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />}
                    onClick={handleMenteesSearch}
                    style={{ width: '100%' }}
                  >
                    Ara
                  </Button>
                </Col>
                <Col xs={24} sm={6} md={4}>
                  <Button
                    icon={<FilterOutlined />}
                    onClick={() => setAdvancedFiltersVisible(!advancedFiltersVisible)}
                    style={{ width: '100%' }}
                  >
                    {advancedFiltersVisible ? 'Filtreleri Gizle' : 'Filtreler'}
                  </Button>
                </Col>
              </Row>

              {advancedFiltersVisible && (
                <Card style={{ marginBottom: '16px' }}>
                  <Collapse defaultActiveKey={['1']}>
                    <Panel header="Detaylı Filtreleme" key="1">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Yetkinlik</Text>
                          <Input
                            placeholder="Yetkinlik ara"
                            value={menteesFilters.skill || ''}
                            onChange={(e) => setMenteesFilters(prev => ({ ...prev, skill: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Onaylanmış Yetkinlik</Text>
                          <Input
                            placeholder="Onaylanmış yetkinlik ara"
                            value={menteesFilters.approvedSkill || ''}
                            onChange={(e) => setMenteesFilters(prev => ({ ...prev, approvedSkill: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Proje</Text>
                          <Input
                            placeholder="Proje adı ara"
                            value={menteesFilters.project || ''}
                            onChange={(e) => setMenteesFilters(prev => ({ ...prev, project: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Bootcamp Konusu</Text>
                          <Input
                            placeholder="Bootcamp konusu ara"
                            value={menteesFilters.bootcampTopic || ''}
                            onChange={(e) => setMenteesFilters(prev => ({ ...prev, bootcampTopic: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Mentör</Text>
                          <Select
                            placeholder="Mentör seçin"
                            value={menteesFilters.mentorId}
                            onChange={(value) => setMenteesFilters(prev => ({ ...prev, mentorId: value || undefined }))}
                            style={{ width: '100%' }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                          >
                            {allMentors.map(mentor => (
                              <Option key={mentor.id} value={mentor.id}>
                                {mentor.firstName} {mentor.lastName}
                              </Option>
                            ))}
                          </Select>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Min Mentor Saati</Text>
                          <InputNumber
                            placeholder="Min saat"
                            value={menteesFilters.minMentorHours}
                            onChange={(value) => setMenteesFilters(prev => ({ ...prev, minMentorHours: value || undefined }))}
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Max Mentor Saati</Text>
                          <InputNumber
                            placeholder="Max saat"
                            value={menteesFilters.maxMentorHours}
                            onChange={(value) => setMenteesFilters(prev => ({ ...prev, maxMentorHours: value || undefined }))}
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Min Deneyim (Yıl)</Text>
                          <InputNumber
                            placeholder="Min yıl"
                            value={menteesFilters.minExperience}
                            onChange={(value) => setMenteesFilters(prev => ({ ...prev, minExperience: value || undefined }))}
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Max Deneyim (Yıl)</Text>
                          <InputNumber
                            placeholder="Max yıl"
                            value={menteesFilters.maxExperience}
                            onChange={(value) => setMenteesFilters(prev => ({ ...prev, maxExperience: value || undefined }))}
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Lokasyon</Text>
                          <Input
                            placeholder="Lokasyon ara"
                            value={menteesFilters.location || ''}
                            onChange={(e) => setMenteesFilters(prev => ({ ...prev, location: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24}>
                          <Space>
                            <Button type="primary" onClick={handleMenteesSearch}>
                              Filtrele
                            </Button>
                            <Button onClick={() => {
                              setMenteesFilters({});
                              setMenteesSearchText('');
                            }}>
                              Filtreleri Temizle
                            </Button>
                          </Space>
                        </Col>
                      </Row>
                    </Panel>
                  </Collapse>
                </Card>
              )}

              <Table
                scroll={{ x: 'max-content' }}
                columns={menteeColumns}
                dataSource={mentees}
                rowKey="id"
                loading={tableLoadingMentees}
                pagination={false}
              />

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Pagination
                  current={menteesPagination.current}
                  pageSize={menteesPagination.pageSize}
                  total={menteesTotal}
                  onChange={(page, pageSize) => {
                    setMenteesPagination(prev => ({ 
                      ...prev, 
                      current: page, 
                      pageSize: pageSize || 10 
                    }));
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => 
                    `${range[0]}-${range[1]} / ${total} mentee`
                  }
                />
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Mentörler" key="mentors">
            <Card>
              <Row style={{ marginBottom: '16px' }} gutter={[8, 8]}>
                <Col xs={24} sm={12} md={10}>
                  <Input
                    placeholder="Mentör ara..."
                    prefix={<SearchOutlined />}
                    value={mentorsSearchText}
                    onChange={(e) => setMentorsSearchText(e.target.value)}
                    onPressEnter={handleMentorsSearch}
                    style={{ borderRadius: '6px' }}
                  />
                </Col>
                <Col xs={24} sm={6} md={4}>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />}
                    onClick={handleMentorsSearch}
                    style={{ width: '100%' }}
                  >
                    Ara
                  </Button>
                </Col>
                <Col xs={24} sm={6} md={4}>
                  <Button
                    icon={<FilterOutlined />}
                    onClick={() => setAdvancedFiltersVisible(!advancedFiltersVisible)}
                    style={{ width: '100%' }}
                  >
                    {advancedFiltersVisible ? 'Filtreleri Gizle' : 'Filtreler'}
                  </Button>
                </Col>
              </Row>

              {advancedFiltersVisible && (
                <Card style={{ marginBottom: '16px' }}>
                  <Collapse defaultActiveKey={['1']}>
                    <Panel header="Detaylı Filtreleme" key="1">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Uzmanlık Alanı (Kategori)</Text>
                          <Select
                            placeholder="Kategori seçin"
                            value={(mentorsFilters as any).category || undefined}
                            onChange={(value) => setMentorsFilters(prev => ({ ...prev, category: value || undefined } as any))}
                            style={{ width: '100%' }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                          >
                            {categories.map(category => (
                              <Option key={category.id} value={category.name}>
                                {category.name}
                              </Option>
                            ))}
                          </Select>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Yetkinlik</Text>
                          <Input
                            placeholder="Yetkinlik ara"
                            value={mentorsFilters.skill || ''}
                            onChange={(e) => setMentorsFilters(prev => ({ ...prev, skill: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Min Deneyim (Yıl)</Text>
                          <InputNumber
                            placeholder="Min yıl"
                            value={mentorsFilters.minExperience}
                            onChange={(value) => setMentorsFilters(prev => ({ ...prev, minExperience: value || undefined }))}
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Max Deneyim (Yıl)</Text>
                          <InputNumber
                            placeholder="Max yıl"
                            value={mentorsFilters.maxExperience}
                            onChange={(value) => setMentorsFilters(prev => ({ ...prev, maxExperience: value || undefined }))}
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text strong>Lokasyon</Text>
                          <Input
                            placeholder="Lokasyon ara"
                            value={mentorsFilters.location || ''}
                            onChange={(e) => setMentorsFilters(prev => ({ ...prev, location: e.target.value || undefined }))}
                            allowClear
                          />
                        </Col>
                        <Col xs={24}>
                          <Space>
                            <Button type="primary" onClick={handleMentorsSearch}>
                              Filtrele
                            </Button>
                            <Button onClick={() => {
                              setMentorsFilters({});
                              setMentorsSearchText('');
                            }}>
                              Filtreleri Temizle
                            </Button>
                          </Space>
                        </Col>
                      </Row>
                    </Panel>
                  </Collapse>
                </Card>
              )}

              <Table
                scroll={{ x: 'max-content' }}
                columns={mentorColumns}
                dataSource={mentors}
                rowKey="id"
                loading={tableLoadingMentors}
                pagination={false}
              />

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Pagination
                  current={mentorsPagination.current}
                  pageSize={mentorsPagination.pageSize}
                  total={mentorsTotal}
                  onChange={(page, pageSize) => {
                    setMentorsPagination(prev => ({ 
                      ...prev, 
                      current: page, 
                      pageSize: pageSize || 10 
                    }));
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => 
                    `${range[0]}-${range[1]} / ${total} mentör`
                  }
                />
              </div>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
      </TabListLoading>

      {/* Candidate Detail Modal */}
      <Modal
        title={`${selectedCandidate?.firstName || ''} ${selectedCandidate?.lastName || ''} - Aday Detayları`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCandidate(null);
        }}
        width={1000}
        footer={[
          <Button
            key="favorite"
            onClick={() => {
              if (!selectedCandidate?.id || !selectedCandidate?.type) return;
              handleAddToFavorites(selectedCandidate, selectedCandidate.type);
            }}
            disabled={
              !selectedCandidate?.id ||
              !selectedCandidate?.type ||
              isInFavorites(selectedCandidate.id, selectedCandidate.type)
            }
            loading={addToFavoritesMutation.isPending}
          >
            {selectedCandidate?.id && selectedCandidate?.type && isInFavorites(selectedCandidate.id, selectedCandidate.type)
              ? 'Favoride'
              : 'Favori'}
          </Button>,
          <Button
            key="pool"
            onClick={() => {
              if (!selectedCandidate?.id || !selectedCandidate?.type) return;
              handleAddToPool(selectedCandidate, selectedCandidate.type);
            }}
            disabled={
              !selectedCandidate?.id ||
              !selectedCandidate?.type ||
              isInPool(selectedCandidate.id, selectedCandidate.type)
            }
            loading={addToPoolMutation.isPending}
          >
            {selectedCandidate?.id && selectedCandidate?.type && isInPool(selectedCandidate.id, selectedCandidate.type)
              ? 'Havuzda'
              : 'Havuza Ekle'}
          </Button>,
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedCandidate(null);
          }}>
            Kapat
          </Button>
        ]}
      >
        {candidateDetailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Aday detayları yükleniyor...</div>
          </div>
        ) : selectedCandidate?.detail ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Kişisel Bilgiler" size="small">
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Text strong>Ad Soyad:</Text> {selectedCandidate.detail.firstName} {selectedCandidate.detail.lastName}
                    </Col>
                    <Col span={12}>
                      <Text strong>E-posta:</Text> {selectedCandidate.detail.email}
                    </Col>
                    <Col span={12}>
                      <Text strong>Telefon:</Text> {selectedCandidate.detail.phoneNumber || 'Belirtilmemiş'}
                    </Col>
                    <Col span={12}>
                      <Text strong>Şehir:</Text> {selectedCandidate.detail.city || 'Belirtilmemiş'}
                    </Col>
                    {selectedCandidate.type === 'mentor' && selectedCandidate.detail.title && (
                      <Col span={12}>
                        <Text strong>Ünvan:</Text> {selectedCandidate.detail.title}
                      </Col>
                    )}
                    {selectedCandidate.type === 'mentor' && selectedCandidate.detail.hourlyRate && (
                      <Col span={12}>
                        <Text strong>Saatlik Ücret:</Text> ₺{selectedCandidate.detail.hourlyRate}
                      </Col>
                    )}
                    <Col span={24}>
                      <Text strong>Bio:</Text> {selectedCandidate.detail.bio || 'Belirtilmemiş'}
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              {selectedCandidate.type === 'mentee' && selectedCandidate.mentorHours && selectedCandidate.mentorHours.length > 0 && (
                <Col span={24}>
                  <Card title="Mentör ile çalışma saatleri" size="small">
                    <Space wrap>
                      {selectedCandidate.mentorHours.map((x: { mentorName: string; totalHours: number }, i: number) => (
                        <Tag key={i} color="blue">{x.mentorName}: {x.totalHours} saat</Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              {selectedCandidate.type === 'mentee' && selectedCandidate.bootcampTitles && selectedCandidate.bootcampTitles.length > 0 && (
                <Col span={24}>
                  <Card title="Katıldığı bootcampler" size="small">
                    <Space wrap>
                      {selectedCandidate.bootcampTitles.map((title: string, i: number) => (
                        <Tag key={i} color="green">{title}</Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.skills && selectedCandidate.detail.skills.length > 0 && (
                <Col span={24}>
                  <Card title="Yetenekler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.skills.map((skill: any, index: number) => (
                        <Tag key={index} color="blue">{skill.skillName}</Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.educations && selectedCandidate.detail.educations.length > 0 && (
                <Col span={24}>
                  <Card title="Eğitim Bilgileri" size="small">
                    {selectedCandidate.detail.educations.map((edu: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{edu.schoolName}</Text> - {edu.degree || edu.department}
                        <br />
                        <Text type="secondary">{(edu.department || edu.fieldOfStudy || '')} • {dayjs(edu.startDate).format('YYYY')} - {edu.endDate ? dayjs(edu.endDate).format('YYYY') : (edu.isGraduated ? '-' : 'Günümüz')}</Text>
                        {edu.description && <div style={{ marginTop: '4px' }}>{edu.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.workExperiences && selectedCandidate.detail.workExperiences.length > 0 && (
                <Col span={24}>
                  <Card title="İş Deneyimi" size="small">
                    {selectedCandidate.detail.workExperiences.map((work: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{work.companyName}</Text> - {work.position}
                        <br />
                        <Text type="secondary">{dayjs(work.startDate).format('MMM YYYY')} - {(work.isCurrentJob || !work.endDate) ? 'Günümüz' : dayjs(work.endDate).format('MMM YYYY')}</Text>
                        {work.description && <div style={{ marginTop: '4px' }}>{work.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.projects && selectedCandidate.detail.projects.length > 0 && (
                <Col span={24}>
                  <Card title="Projeler" size="small">
                    {selectedCandidate.detail.projects.map((project: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{project.projectName}</Text>
                        <br />
                        <Text type="secondary">{dayjs(project.startDate).format('MMM YYYY')} - {project.endDate ? dayjs(project.endDate).format('MMM YYYY') : 'Devam ediyor'}</Text>
                        {project.projectDescription && <div style={{ marginTop: '4px' }}>{project.projectDescription}</div>}
                        {project.technologies && (
                          <div style={{ marginTop: '4px' }}>
                            <Space wrap>
                              {(Array.isArray(project.technologies) ? project.technologies : project.technologies.split(',')).map((tech: string, techIndex: number) => (
                                <Tag key={techIndex} color="green">{tech.trim()}</Tag>
                              ))}
                            </Space>
                          </div>
                        )}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.certificates && selectedCandidate.detail.certificates.length > 0 && (
                <Col span={24}>
                  <Card title="Sertifikalar" size="small">
                    {selectedCandidate.detail.certificates.map((cert: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{cert.certificateName}</Text>
                        <br />
                        <Text type="secondary">{cert.issuingOrganization} • {dayjs(cert.issueDate).format('MMM YYYY')}</Text>
                        {cert.description && <div style={{ marginTop: '4px' }}>{cert.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {selectedCandidate.type === 'mentor' && selectedCandidate.detail.favouriteMentees && selectedCandidate.detail.favouriteMentees.length > 0 && (
                <Col span={24}>
                  <Card title="Referans verdiği menteeler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.favouriteMentees.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button key={m.id} type="link" size="small" onClick={() => window.open(`/mentee/${m.id}`, '_blank')}>
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              {selectedCandidate.type === 'mentee' && selectedCandidate.detail.referencedByMentors && selectedCandidate.detail.referencedByMentors.length > 0 && (
                <Col span={24}>
                  <Card title="Referans veren mentörler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.referencedByMentors.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button key={m.id} type="link" size="small" onClick={() => window.open(`/mentor/${m.id}`, '_blank')}>
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text type="secondary">Aday detayları yüklenemedi</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CandidateSearchTab;
